import { ClientTagFilter } from "../ClientTags/ClientTags";
import { canManageClientTags } from "intake_shared/src/client_tags";
import { PageHeader } from "../../bits/PageHeader/PageHeader";
import * as React from "react";
import { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory, useLocation } from "react-router-dom";
import { AclContext } from "../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import {
    AppSortOrder,
    ClientStatus,
    IClient,
    IClinician,
    SortConfigProps,
} from "intake_shared/src/types";
import { Button } from "../../bits/Button/Button";
import SearchIcon from "../../icons/Search";
import { getStringVariations, isValidEmail } from "../../../../utils/utils";
import store from "../../../../store";
import { formatPhoneNumber } from "react-phone-number-input";
import ClientList from "./ClientList/ClientList";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { ClientStatusLabels } from "../../../../utils/labels";
import FilterButton from "../../bits/FilterButton/FilterButton";
import RefreshIcon from "../../icons/Refresh";
import Skeleton from "../../bits/Skeleton/Skeleton";
import { DatePicker } from "../../bits/DatePicker/DatePicker";
import { TablePagination } from "../../bits/Table/TablePagination";
import styles from "./IntakeQueue.module.scss";
import TimesIcon from "../../icons/Times";
import { Input } from "../../bits/Input/Input";
import PhoneInput from "../../bits/PhoneInput/PhoneInput";
import { defaultClient } from "../ClientDisplayDialog/ClientDisplayDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import LeaHandoffDialog from "../communication/LeaConversation/LeaHandoffDialog";
import { canHandToLea } from "../communication/LeaConversation/leaHandoff";
import CompleteClientFlow from "../client-forms/CompleteClientFlow/CompleteClientFlow";
import type { TableSortBy } from "../../bits/Table/Table";
import {
    getIntakeQueueNavigationState,
    getIntakeQueueQueryState,
    getRestoredIntakeQueueDate,
    serializeIntakeQueueSearchState,
    updateIntakeQueueSearch,
} from "./intake_queue_state";

const CANNOT_FILTER_BY: ClientStatus[] = [
    ClientStatus.WAITLIST,
    ClientStatus.COMPLETED,
    ClientStatus.DO_NOT_BOOK,
];

const PAGE_LIMIT = 50;
const SORT_ORDER_QUEUE_LABEL = "queueSortOrder";
const AI_DRAFT_ENABLED = process.env.REACT_APP_AI_INTAKE_DRAFT_ENABLED === "true";
const excludeCompletedClients = (clients: IClient[]) =>
    clients.filter((client) => client.status !== ClientStatus.COMPLETED);

export default function IntakeQueue() {
    const [cookies, setCookies] = useCookies([SORT_ORDER_QUEUE_LABEL]);
    const browserLocation = useLocation();
    const history = useHistory();

    const userAcl = useContext(AclContext)?.authUser?.acl;
    const aclContext = React.useContext(AclContext);
    const loggedInUserEmail = useContext(AclContext)?.authUser.user?.email;
    const { addToast } = useToasts();
    const initialQueueState = getIntakeQueueQueryState(browserLocation.search);
    const navigationState = getIntakeQueueNavigationState(browserLocation.state);
    const restoredSearchState = navigationState.intakeQueue;
    const [selectedItems, setSelectedItems] = useState<string[]>(
        initialQueueState.assignees
    );
    const [selectedTags, setSelectedTags] = useState(initialQueueState.tags ?? []);
    const [clients, setClients] = useState([] as IClient[]);
    const [cliniciansLookup, setCliniciansLookup] = useState<
        Record<string, IClinician>
    >({});
    const [assigneeCounts, setAssigneeCounts] = useState<Record<string, number>>({});
    const [sortConfig, setSortConfig] = useState<SortConfigProps>({
        key:
            initialQueueState.sortKey ||
            localStorage.getItem("sort_attr") ||
            "date_updated",
        direction:
            initialQueueState.sortDirection ||
            (localStorage.getItem("sort_order") as AppSortOrder) ||
            AppSortOrder.ASC,
    });
    const [sortBy, setSortBy] = useState(
        (initialQueueState.queueOrder ||
            cookies[SORT_ORDER_QUEUE_LABEL] ||
            AppSortOrder.ASC) as "asc" | "desc"
    );
    const [showOnlyAssignedClients, setShowOnlyAssignedClients] = useState(
        initialQueueState.assignees.length > 0
    );
    const [tableSort, setTableSort] = useState<TableSortBy | undefined>(
        initialQueueState.tableSort
    );

    const [searchByEmail, setSearchByEmail] = useState(
        restoredSearchState?.searchByEmail ?? ""
    );
    const [searchByLastName, setSearchByLastName] = useState(
        restoredSearchState?.searchByLastName ?? ""
    );
    const [searchByPhoneNo, setSearchByPhoneNo] = useState(
        restoredSearchState?.searchByPhoneNo ?? ""
    );
    const [searchByDob, setSearchByDob] = useState<Date | null>(
        getRestoredIntakeQueueDate(restoredSearchState?.searchByDob)
    );
    const [loading, setLoading] = useState(false);
    const [handoffClient, setHandoffClient] = useState<IClient | null>(null);
    const [filterByStatus, setFilterByStatus] = useState(
        initialQueueState.status || ClientStatus.INCOMPLETE_INQUIRY
    );

    const [currentPage, setCurrentPage] = useState(initialQueueState.page);
    const [isSearchActive, setIsSearchActive] = useState(
        restoredSearchState?.hasSearched === true
    );
    const shouldRestoreSearch = React.useRef(
        restoredSearchState?.hasSearched === true
    );

    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const [showCloseClientDialog, setShowCloseClientDialog] = useState(false);
    const [selectedClientsToClose, setSelectedClientsToClose] = useState<IClient[]>(
        []
    );

    const updateQueueQuery = React.useCallback(
        (
            updates: Record<string, string | undefined>,
            hasSearched = isSearchActive
        ) => {
            history.replace({
                pathname: browserLocation.pathname,
                search: updateIntakeQueueSearch(browserLocation.search, updates),
                state: {
                    ...navigationState,
                    intakeQueue: {
                        ...serializeIntakeQueueSearchState({
                            searchByEmail,
                            searchByLastName,
                            searchByPhoneNo,
                            searchByDob,
                        }),
                        hasSearched,
                    },
                },
            });
        },
        [
            browserLocation.pathname,
            browserLocation.search,
            history,
            isSearchActive,
            navigationState,
            searchByDob,
            searchByEmail,
            searchByLastName,
            searchByPhoneNo,
        ]
    );

    React.useEffect(() => {
        if (filterByStatus !== ClientStatus.APPT_HELD) {
            return;
        }

        const clinicianIds = Array.from(
            new Set(
                clients
                    .filter((client) => client.status === ClientStatus.APPT_HELD)
                    .flatMap((client) =>
                        client.held_bookings.map((booking) => booking.clinician_id)
                    )
            )
        ).filter(Boolean);
        let cancelled = false;

        const fetchClinicians = async () => {
            if (clinicianIds.length === 0) {
                setCliniciansLookup({});
                return;
            }

            try {
                const clinicians = await store.fetchCliniciansByIds([
                    ...clinicianIds,
                ]);
                if (cancelled) {
                    return;
                }

                setCliniciansLookup(
                    clinicians.reduce<Record<string, IClinician>>(
                        (lookup, clinician) => {
                            if (clinician.id) {
                                lookup[clinician.id] = clinician;
                            }
                            return lookup;
                        },
                        {}
                    )
                );
            } catch {
                if (!cancelled) {
                    setCliniciansLookup({});
                }
            }
        };

        fetchClinicians();

        return () => {
            cancelled = true;
        };
    }, [clients, filterByStatus]);

    const fetchClients = React.useCallback(
        async (status: ClientStatus) => {
            setLoading(true);
            const { clients: returnedClients, assigneeCounts: counts } =
                await store.fetchAllClientsByStatus(
                    [status],
                    sortBy,
                    sortConfig,
                    currentPage,
                    PAGE_LIMIT,
                    selectedTags
                );

            if (!cookies.queueSortOrder || cookies.queueSortOrder !== sortBy) {
                setCookies(SORT_ORDER_QUEUE_LABEL, sortBy);
            }
            setClients(excludeCompletedClients(returnedClients));
            setAssigneeCounts(counts);
            setNextButtonDisabled(returnedClients.length < PAGE_LIMIT);
            setLoading(false);
        },
        [
            sortConfig,
            currentPage,
            cookies.queueSortOrder,
            sortBy,
            setCookies,
            selectedTags,
        ]
    );

    const handleFirstPageClick = () => {
        setCurrentPage(1);
        updateQueueQuery({ page: "1" });
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            const previousPage = currentPage - 1;
            setCurrentPage(previousPage);
            updateQueueQuery({ page: previousPage.toString() });
        }
    };

    const handleNextClick = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        updateQueueQuery({ page: nextPage.toString() });
    };

    const { intakeOfficers = [] } = aclContext?.intakeConfig || {};

    const options = ["Me", ...intakeOfficers];

    const fetchAssignedClients = React.useCallback(async () => {
        const processedItems = selectedItems.map((item) =>
            item === "Me" ? loggedInUserEmail : item
        );

        setLoading(true);
        try {
            const { clients: assignedClients, assigneeCounts: counts } =
                (await store.getAllAssignedClientsByEmail(
                    "assigned_to",
                    processedItems,
                    "secondary_assignee",
                    processedItems,
                    filterByStatus,
                    currentPage,
                    PAGE_LIMIT,
                    selectedTags
                )) || { clients: [], assigneeCounts: {} };

            const activeClients = excludeCompletedClients(assignedClients);
            setNextButtonDisabled(activeClients.length < PAGE_LIMIT);
            setClients(activeClients);
            setAssigneeCounts(counts);
        } finally {
            setLoading(false);
        }
    }, [
        currentPage,
        filterByStatus,
        loggedInUserEmail,
        selectedItems,
        selectedTags,
    ]);

    // Single queue loader. A second effect that also called fetchClients used to
    // run alongside this one, so every load scanned the status bucket twice.
    React.useEffect(() => {
        if (isSearchActive) return;

        if (showOnlyAssignedClients) {
            fetchAssignedClients();
        } else {
            fetchClients(filterByStatus);
        }
    }, [
        isSearchActive,
        showOnlyAssignedClients,
        filterByStatus,
        sortBy,
        fetchClients,
        fetchAssignedClients,
        currentPage,
    ]);

    const handleRefresh = () => {
        updateQueueQuery({}, false);

        if (isSearchActive) {
            setIsSearchActive(false);
            return;
        }

        if (selectedItems.length > 0) {
            fetchAssignedClients();
        } else {
            fetchClients(filterByStatus);
        }
    };

    const handleSelect = (item: string) => {
        let updatedSelectedItems = [...selectedItems];

        if (updatedSelectedItems.includes(item)) {
            updatedSelectedItems = updatedSelectedItems.filter((i) => i !== item);
        } else {
            if (updatedSelectedItems.length < 2) {
                updatedSelectedItems.push(item);
            }
        }
        setIsSearchActive(false);
        setSelectedItems(updatedSelectedItems);
        setShowOnlyAssignedClients(updatedSelectedItems.length > 0);
        setCurrentPage(1);
        updateQueueQuery(
            {
                assignees: updatedSelectedItems.join(",") || undefined,
                page: "1",
            },
            false
        );
    };

    const clearAssignees = () => {
        setSelectedItems([]);
        setShowOnlyAssignedClients(false);
        setCurrentPage(1);
        updateQueueQuery({ assignees: undefined, page: "1" });
    };

    const handleSortConfigChange: React.Dispatch<
        React.SetStateAction<SortConfigProps>
    > = (value) => {
        const updatedSortConfig =
            typeof value === "function" ? value(sortConfig) : value;
        setSortConfig(updatedSortConfig);
        localStorage.setItem("sort_attr", updatedSortConfig.key);
        localStorage.setItem("sort_order", updatedSortConfig.direction);
        setTableSort(undefined);
        updateQueueQuery({
            sortKey: updatedSortConfig.key,
            sortDirection: updatedSortConfig.direction,
            tableSortKey: undefined,
            tableSortDirection: undefined,
        });
    };

    const handleTableSortChange = React.useCallback(
        (updatedTableSort?: TableSortBy) => {
            if (updatedTableSort && updatedTableSort.id !== "appt_start_date") {
                return;
            }
            if (
                tableSort?.id === updatedTableSort?.id &&
                tableSort?.desc === updatedTableSort?.desc
            ) {
                return;
            }

            setTableSort(updatedTableSort);
            updateQueueQuery({
                tableSortKey: updatedTableSort?.id,
                tableSortDirection: updatedTableSort
                    ? updatedTableSort.desc
                        ? AppSortOrder.DESC
                        : AppSortOrder.ASC
                    : undefined,
            });
        },
        [tableSort?.desc, tableSort?.id, updateQueueQuery]
    );

    const handleCloseClientsClick = (selectedClients: IClient[]) => {
        if (selectedClients.length === 0) {
            addToast("Please select at least one client to close.", {
                appearance: "error",
            });
            return;
        }

        if (
            selectedClients.some(
                (client) => client.status === ClientStatus.INS_VERIFICATION
            )
        ) {
            addToast(
                "Cannot close clients because one or more of the selected clients is in verification.",
                {
                    appearance: "error",
                }
            );
            return;
        }

        const hasCompletedClients = selectedClients.some(
            (client) => client.status === ClientStatus.COMPLETED
        );

        if (hasCompletedClients) {
            addToast(
                "Cannot close clients because one or more of the selected clients is already closed.",
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
            return;
        }

        setSelectedClientsToClose(selectedClients);
        setShowCloseClientDialog(true);
    };

    const handleFetchClients = async () => {
        setLoading(true);
        try {
            await fetchClients(filterByStatus);
        } finally {
            setLoading(false);
        }
    };

    const searchByEmailQuery = searchByEmail.trim();
    const searchByLastNameQuery = searchByLastName.trim();
    const searchByPhoneNoQuery = searchByPhoneNo.trim();
    const hasSearchCriteria = Boolean(
        searchByEmailQuery ||
            searchByLastNameQuery ||
            searchByPhoneNoQuery ||
            searchByDob
    );

    const searchClients = React.useCallback(
        async (showResultToast: boolean) => {
            const matchingClients = isValidEmail(searchByEmailQuery)
                ? await store.fetchClientsByEmails(
                      getStringVariations(searchByEmailQuery),
                      selectedTags
                  )
                : await store.findClientsByFirstAndLastName({
                      firstName: searchByEmailQuery,
                      lastName: searchByLastNameQuery,
                      phoneNo: formatPhoneNumber(searchByPhoneNoQuery),
                      dateOfBirth: searchByDob,
                      tagKeys: selectedTags,
                  });
            const activeClients = excludeCompletedClients(matchingClients);
            if (showResultToast) {
                if (activeClients.length) {
                    addToast(
                        `${activeClients.length} matching client${
                            activeClients.length === 1 ? "" : "s"
                        } found`,
                        {
                            appearance: "success",
                        }
                    );
                } else {
                    addToast("No matching clients found", {
                        appearance: "error",
                    });
                }
            }
            setNextButtonDisabled(true);
            const counts: Record<string, number> = {};
            for (const client of activeClients) {
                if (client.assigned_to) {
                    counts[client.assigned_to] =
                        (counts[client.assigned_to] ?? 0) + 1;
                }
                if (client.secondary_assignee) {
                    counts[client.secondary_assignee] =
                        (counts[client.secondary_assignee] ?? 0) + 1;
                }
            }
            setAssigneeCounts(counts);
            setClients(activeClients);
            setIsSearchActive(true);
        },
        [
            addToast,
            searchByDob,
            selectedTags,
            searchByEmailQuery,
            searchByLastNameQuery,
            searchByPhoneNoQuery,
        ]
    );

    React.useEffect(() => {
        if (!shouldRestoreSearch.current) {
            return;
        }

        shouldRestoreSearch.current = false;
        const restoreSearch = async () => {
            setLoading(true);
            try {
                await searchClients(false);
            } catch {
                setIsSearchActive(false);
                addToast("Unable to restore client search. Please try again.", {
                    appearance: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        restoreSearch();
    }, [addToast, searchClients]);

    const handleSearchSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!hasSearchCriteria) {
            addToast(
                "Please enter an email, name, phone number, or date of birth.",
                {
                    appearance: "error",
                }
            );
            return;
        }

        setLoading(true);
        try {
            await searchClients(true);
            updateQueueQuery({}, true);
        } catch (error) {
            addToast("Unable to search clients. Please try again.", {
                appearance: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseComplete = async (client?: IClient, close?: boolean) => {
        if (close === false) {
            return;
        } else {
            setShowCloseClientDialog(false);
            await handleFetchClients();
            setSelectedClientsToClose([]);
        }
    };

    return (
        <div className="v2-page">
            <PageHeader
                title="Intake Queue"
                meta={
                    loading
                        ? undefined
                        : `${
                              clients.length >= PAGE_LIMIT
                                  ? PAGE_LIMIT + "+"
                                  : clients.length
                          } clients ${
                              selectedItems.length
                                  ? "for selected assignees"
                                  : `in ${
                                        ClientStatusLabels[filterByStatus] ||
                                        filterByStatus
                                    }`
                          }`
                }
            />
            <div className={styles.filters}>
                <div className={styles.filterTop}>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton text={filterByStatus} />
                        </Dropdown.Trigger>
                        <Dropdown.Content className={styles.filterTop_dropdown}>
                            {Object.values(ClientStatus)
                                .filter((s) => !CANNOT_FILTER_BY.includes(s))
                                .map((s) => (
                                    <Dropdown.Item
                                        key={`status-${s}`}
                                        closeMenuOnSelect
                                        isSelected={s === filterByStatus}
                                        onClick={() => {
                                            if (
                                                s === ClientStatus.INS_VERIFICATION
                                            ) {
                                                setSortConfig({
                                                    ...sortConfig,
                                                    key: "insurance_provider",
                                                });
                                                localStorage.setItem(
                                                    "sort_attr",
                                                    "insurance_provider"
                                                );
                                            }
                                            setIsSearchActive(false);
                                            setFilterByStatus(s as ClientStatus);
                                            setCurrentPage(1);
                                            setTableSort(undefined);
                                            localStorage.setItem("status", s);
                                            updateQueueQuery(
                                                {
                                                    status: s,
                                                    page: "1",
                                                    sortKey:
                                                        s ===
                                                        ClientStatus.INS_VERIFICATION
                                                            ? "insurance_provider"
                                                            : sortConfig.key,
                                                    tableSortKey: undefined,
                                                    tableSortDirection: undefined,
                                                },
                                                false
                                            );
                                        }}
                                    >
                                        {ClientStatusLabels[s] || s}
                                    </Dropdown.Item>
                                ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                text={
                                    sortBy === "asc"
                                        ? "Oldest first"
                                        : "Newest first"
                                }
                            />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <>
                                <Dropdown.Item
                                    closeMenuOnSelect
                                    isSelected={sortBy === "asc"}
                                    onClick={() => {
                                        setSortConfig({
                                            ...sortConfig,
                                            direction: "" as AppSortOrder,
                                            key: "date_updated",
                                        });
                                        setIsSearchActive(false);
                                        setSortBy("asc");
                                        setTableSort(undefined);
                                        localStorage.setItem(
                                            "sort_attr",
                                            "date_updated"
                                        );
                                        localStorage.setItem("sort_order", "asc");
                                        updateQueueQuery(
                                            {
                                                queueOrder: AppSortOrder.ASC,
                                                sortKey: "date_updated",
                                                sortDirection: undefined,
                                                tableSortKey: undefined,
                                                tableSortDirection: undefined,
                                            },
                                            false
                                        );
                                    }}
                                >
                                    Oldest first
                                </Dropdown.Item>
                                <Dropdown.Item
                                    closeMenuOnSelect
                                    isSelected={sortBy === "desc"}
                                    onClick={() => {
                                        setSortConfig({
                                            ...sortConfig,
                                            direction: "" as AppSortOrder,
                                            key: "date_updated",
                                        });
                                        setIsSearchActive(false);
                                        setSortBy("desc");
                                        setTableSort(undefined);
                                        localStorage.setItem(
                                            "sort_attr",
                                            "date_updated"
                                        );
                                        localStorage.setItem("sort_order", "desc");
                                        updateQueueQuery(
                                            {
                                                queueOrder: AppSortOrder.DESC,
                                                sortKey: "date_updated",
                                                sortDirection: undefined,
                                                tableSortKey: undefined,
                                                tableSortDirection: undefined,
                                            },
                                            false
                                        );
                                    }}
                                >
                                    Newest first
                                </Dropdown.Item>
                            </>
                        </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton text="Assignees" />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item
                                values={selectedItems}
                                onClick={clearAssignees}
                            >
                                Clear
                            </Dropdown.Item>
                            {options.map((s) => (
                                <Dropdown.Item
                                    key={s}
                                    itemValue={s}
                                    values={selectedItems}
                                    isMulti
                                    isSelected={selectedItems.includes(s)}
                                    onClick={() => handleSelect(s)}
                                >
                                    {s}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    {canManageClientTags(userAcl) && (
                        <ClientTagFilter
                            value={selectedTags}
                            onChange={(tags) => {
                                setSelectedTags(tags);
                                setCurrentPage(1);
                                setIsSearchActive(false);
                                updateQueueQuery(
                                    { tags: tags.join(",") || undefined, page: "1" },
                                    false
                                );
                            }}
                        />
                    )}
                    <form
                        className={styles.filterTop_search}
                        onSubmit={handleSearchSubmit}
                    >
                        <div className={styles.filterTop_flex}>
                            <Input
                                placeholder="Email/first name"
                                value={searchByEmail}
                                onChange={(e) => {
                                    setSearchByEmail(e.target.value);
                                }}
                                className={styles.filterTop_input}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <Input
                                placeholder="Last name"
                                value={searchByLastName}
                                onChange={(e) => {
                                    setSearchByLastName(e.target.value);
                                }}
                                className={styles.filterTop_input}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <PhoneInput
                                placeholder="Phone no"
                                value={searchByPhoneNo}
                                className={styles.filterTop_input}
                                onChange={(phoneNo: string | undefined) =>
                                    setSearchByPhoneNo(phoneNo || "")
                                }
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <DatePicker
                                dateFormat={"MM/dd/yyyy"}
                                selected={searchByDob}
                                onChange={(date) => setSearchByDob(date as Date)}
                                placeholderText="Date of birth"
                                inputSize="small"
                            />
                        </div>
                        <div className={styles.filterTop_actions}>
                            {(userAcl?.intake || userAcl?.super_admin) && (
                                <Button
                                    onClick={() => {
                                        history.push({
                                            pathname: `/v2/client-full-view`,
                                            search: `?client_type=new_client&activeTab=${
                                                ClientStatus.INCOMPLETE_INQUIRY
                                            }&page=${1}`,
                                            state: { client: defaultClient },
                                        });
                                    }}
                                >
                                    Add Client
                                </Button>
                            )}
                            <Button
                                variant="secondary"
                                size="icon"
                                title="Refresh list"
                                aria-label="Refresh list"
                                onClick={handleRefresh}
                            >
                                <RefreshIcon />
                            </Button>
                            <Button
                                type="submit"
                                size="icon"
                                title="Search"
                                aria-label="Search"
                                disabled={!hasSearchCriteria}
                            >
                                <SearchIcon />
                            </Button>
                        </div>
                    </form>
                </div>
                <div className={styles.filterBottom}>
                    {selectedItems.length > 0 && (
                        <div className={styles.selectedItems}>
                            <span className={styles.selectedItems_label}>
                                Assignees:
                            </span>
                            {selectedItems.map((email) => email).join(",")}
                            <Button
                                variant="normal"
                                className={styles.selectedItems_clear}
                                onClick={clearAssignees}
                            >
                                <TimesIcon
                                    className={styles.selectedItems_clearIcon}
                                    stroke="#576a81"
                                />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.clients}>
                {loading ? (
                    <Skeleton />
                ) : (
                    <ClientList
                        status={filterByStatus}
                        clients={clients}
                        hideStatusColumn={!isSearchActive && !selectedItems.length}
                        cliniciansLookup={
                            filterByStatus === ClientStatus.APPT_HELD
                                ? cliniciansLookup
                                : undefined
                        }
                        assigneeCounts={assigneeCounts}
                        pageNumber={currentPage}
                        enableOtherStatuses={true}
                        setSortConfig={handleSortConfigChange}
                        sortConfig={sortConfig}
                        tableSort={tableSort}
                        onTableSortChange={handleTableSortChange}
                        allowSelect={
                            filterByStatus !== ClientStatus.INS_VERIFICATION
                        }
                        onSelectActions={(rows: IClient[]) => [
                            <Dropdown.Item
                                key="close-client"
                                onClick={() => handleCloseClientsClick(rows)}
                            >
                                Close client
                            </Dropdown.Item>,
                            ...(AI_DRAFT_ENABLED &&
                            (userAcl?.intake || userAcl?.super_admin) &&
                            rows.length === 1 &&
                            canHandToLea(rows[0])
                                ? [
                                      <Dropdown.Item
                                          key="hand-to-ai"
                                          onClick={() => setHandoffClient(rows[0])}
                                      >
                                          Hand to Lea AI
                                      </Dropdown.Item>,
                                  ]
                                : []),
                        ]}
                        showActionsInHeader
                    />
                )}

                {clients.length > 0 && !isSearchActive && (
                    <TablePagination
                        currentPage={currentPage}
                        onFirstPage={handleFirstPageClick}
                        onPrevPage={handlePrevClick}
                        onNextPage={handleNextClick}
                        isNextDisabled={nextButtonDisabled}
                        showPrevButton={currentPage > 1}
                        buttonClassName={styles.footer_btn}
                    />
                )}
            </div>
            {handoffClient && (
                <LeaHandoffDialog
                    client={handoffClient}
                    onClose={() => setHandoffClient(null)}
                />
            )}
            {showCloseClientDialog && selectedClientsToClose.length > 0 && (
                <Dialog
                    open={showCloseClientDialog}
                    onClose={() => {
                        setShowCloseClientDialog(false);
                        setSelectedClientsToClose([]);
                    }}
                    title={`Close ${selectedClientsToClose.length} ${
                        selectedClientsToClose.length === 1 ? "client" : "clients"
                    }`}
                    showFooter={false}
                >
                    <CompleteClientFlow
                        title={`Close ${selectedClientsToClose.length} ${
                            selectedClientsToClose.length === 1
                                ? "client"
                                : "clients"
                        }`}
                        clientProp={
                            selectedClientsToClose.find(
                                (c) => c.status !== ClientStatus.COMPLETED
                            ) || selectedClientsToClose[0]
                        }
                        expectedCaller="admin"
                        showCompletionReason
                        showCompletionNotes
                        onSaveClient={handleCloseComplete}
                        hideCard
                        disableInternalDialogs={false}
                        completeBtnText={`Close ${selectedClientsToClose.length} ${
                            selectedClientsToClose.length === 1
                                ? "client"
                                : "clients"
                        }`}
                        isBulkOperation={selectedClientsToClose.length > 1}
                        bulkClients={selectedClientsToClose}
                        onBulkComplete={handleCloseComplete}
                    />
                </Dialog>
            )}
        </div>
    );
}
